import { useEffect, useRef, useState } from "react";
import { dispatchTask } from "../state";
import type { Lane } from "../types";

// Format: "<lane>: <task>". Autocompletes lane id from the first token.
export function CommandPalette({ onClose, lanes }: { onClose: () => void; lanes: Lane[] }) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const ref = useRef<HTMLInputElement | null>(null);

  useEffect(() => { ref.current?.focus(); }, []);

  const lanePart = (value.split(":")[0] || "").trim().toLowerCase();
  const suggestions = lanePart && !value.includes(":")
    ? lanes.filter((l) => l.id.startsWith(lanePart) || l.name.toLowerCase().startsWith(lanePart)).slice(0, 6)
    : [];

  async function submit() {
    setErr(null);
    const m = /^\s*([a-z]+)\s*:\s*(.+)\s*$/i.exec(value);
    if (!m) { setErr('Format: "<lane>: <task>"'); return; }
    const lane = m[1].toLowerCase();
    const task = m[2];
    if (!lanes.some((l) => l.id === lane)) { setErr(`Unknown lane "${lane}"`); return; }
    setBusy(true);
    const res = await dispatchTask(lane, task);
    setBusy(false);
    if (!res.ok) { setErr(res.error || "Dispatch failed"); return; }
    onClose();
  }

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center pt-32 bg-black/50" onClick={onClose}>
      <div
        className="w-[640px] max-w-[92vw] bg-panel border border-edge rounded-lg overflow-hidden shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          ref={ref}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
            if (e.key === "Escape") onClose();
            if (e.key === "Tab" && suggestions.length) {
              e.preventDefault();
              setValue(`${suggestions[0].id}: `);
            }
          }}
          placeholder='e.g.  tess: check dispatcher health'
          className="w-full bg-transparent px-4 py-3 text-base outline-none placeholder:text-mist/60"
          disabled={busy}
        />
        {suggestions.length > 0 && (
          <div className="border-t border-edge text-sm">
            {suggestions.map((l) => (
              <button
                key={l.id}
                onClick={() => { setValue(`${l.id}: `); ref.current?.focus(); }}
                className="w-full text-left px-4 py-1.5 hover:bg-edge flex items-center gap-2"
              >
                <span>{l.emoji}</span>
                <span>{l.name}</span>
                <span className="text-mist text-xs ml-auto font-mono">{l.id}</span>
              </button>
            ))}
          </div>
        )}
        <div className="border-t border-edge px-4 py-2 text-xs text-mist flex justify-between">
          <span>Enter to dispatch · Esc to close · Tab to autocomplete</span>
          {err && <span className="text-bad">{err}</span>}
          {busy && <span>Sending…</span>}
        </div>
      </div>
    </div>
  );
}
